import { CdkDragDrop, CdkDropList } from "@angular/cdk/drag-drop";
import { Component, Signal, WritableSignal, computed, inject, signal } from "@angular/core";
import { takeUntilDestroyed, toSignal } from "@angular/core/rxjs-interop";
import { DragAndDropService } from "@app/drag-and-drop/drag-and-drop.service";
import { IBaseCard } from "@app/views-interfaces";
import { ITaxReturnStatus } from "@app/views-interfaces/tax-return-status-model.interface";
import { TranslateService, TranslatePipe } from "@ngx-translate/core";
import { FixedScaleAxis } from "chartist";
import { BarChartConfiguration, PieChartConfiguration, ChartistComponent } from "ng-chartist";
import { AnalyticsService, AuthService, CountersService, DashboardsService, LocalStorageService, StepsService } from "../shared";
import { CardViewService, ViewType } from "../shared/card-view-service/card-view.service";
import { HelpersService } from "../shared/helpers-service/helpers.service";
import { isiFirmTax, isJobsBilling } from "../views-interfaces/ApplicationType.enum";
import { IDashboardModel } from "../views-interfaces/dashboard-model.interface";
import { CardDragAndDropService } from "@app/shared/card-drag-and-drop-service";
import { JumpstartComponentsWkIconsModule, JumpstartComponentsModule } from "@wk/components-v3-angular17";
import { DropListComponent } from "../drag-and-drop/drop-list/drop-list.component";
import { RouterLink } from "@angular/router";
import { AppLoaderComponent } from "../shared/components/app-loader/app-loader.component";
import { UpperCasePipe } from "@angular/common";
@Component({
    // eslint-disable-next-line @angular-eslint/component-selector
    selector: "app-header-component",
    templateUrl: "./header.component.html",
    styleUrl: "./header.component.scss",
    imports: [
        JumpstartComponentsWkIconsModule,
        CdkDropList,
        DropListComponent,
        RouterLink,
        JumpstartComponentsModule,
        ChartistComponent,
        AppLoaderComponent,
        UpperCasePipe,
        TranslatePipe,
    ],
})
export class HeaderComponent {
    private readonly analyticsService = inject(AnalyticsService);
    private readonly authService = inject(AuthService);
    protected readonly cardViewService = inject(CardViewService);
    private readonly cardDragAndDropService = inject(CardDragAndDropService);
    private readonly countersService = inject(CountersService);
    private readonly dashboardsService = inject(DashboardsService);
    private readonly dragAndDropService = inject(DragAndDropService);
    private readonly helpersService = inject(HelpersService);
    private readonly stepsService = inject(StepsService);
    private readonly translateService = inject(TranslateService);

    currentLanguage = "en-CA";
    currentCounters = {
        notStarted: 0,
        active: 0,
        completed: 0,
        all: 0,
    };
    countersPercentages = {
        notStarted: "0",
        active: "0",
        completed: "0",
    };
    currentTotal = 0;
    variance = {
        notStarted: 0,
        active: 0,
        completed: 0,
    };
    lastYearCountersByState = {
        notStarted: 0,
        active: 0,
        completed: 0,
    };
    cardsWithWarning: number;
    cardsWithAlert: number;
    pieChartConfiguration: PieChartConfiguration;
    activeBarConfiguration: BarChartConfiguration;
    showLastYear: boolean;
    statsVisible: boolean;
    isAllView: boolean;
    barChartHigh = 50;
    barChartWidth = 110;

    protected currentDashboard: IDashboardModel;
    showGraph = false;
    showLoadingStatistics = true;
    loadingCards = false;
    protected readonly isJobsBillingDashboard = computed(() => {
        const appType = this.dashboardsService.currentDashboard()?.ApplicationType;
        return appType !== undefined && isJobsBilling(appType);
    });
    protected dragging: Signal<boolean> = this.dragAndDropService.isDragging;
    protected dragOriginalStatusIds: Signal<Set<string>> = this.cardDragAndDropService.dragOriginalStatusIds;
    protected notStartedStatus: WritableSignal<ITaxReturnStatus | null> = signal(null);
    protected activeStatus: WritableSignal<ITaxReturnStatus | null> = signal(null);
    protected completedStatus: WritableSignal<ITaxReturnStatus | null> = signal(null);
    private readonly _viewType = toSignal(this.cardViewService.viewTypeChangeEvent$);
    protected readonly displayAlertCenter = computed<boolean>(() => this._viewType() === ViewType.Active);

    protected readonly disabledDropListIds = computed<Set<string>>(() => {
        const statusIds = [this.notStartedStatus()?.Id, this.activeStatus()?.Id, this.completedStatus()?.Id].filter((id): id is string => !!id);
        return this.cardDragAndDropService.getDisabledStatusDropListIds(statusIds);
    });

    constructor() {
        this.currentLanguage = this.translateService.getCurrentLang()
            ? this.translateService.getCurrentLang()
            : this.translateService.getFallbackLang();

        this.dashboardsService.dashboardChangeEvent$.pipe(takeUntilDestroyed()).subscribe(() => {
            this.showGraph = false;
            this.showLoadingStatistics = true;
            this.resetCounters();
        });

        this.dashboardsService.currentDashboardChanged$.pipe(takeUntilDestroyed()).subscribe(() => {
            this.init();
        });

        this.helpersService.cardDownloadEvent$.pipe(takeUntilDestroyed()).subscribe((status) => {
            this.loadingCards = status;
        });

        this.countersService.countersUpdated$.pipe(takeUntilDestroyed()).subscribe(() => {
            this.refreshAllCounters();
        });

        this.cardViewService.viewTypeChangeEvent$.pipe(takeUntilDestroyed()).subscribe((viewtype) => {
            this.isAllView = viewtype === "all";
        });

        this.cardViewService.viewDisplayModeChangeEvent$.pipe(takeUntilDestroyed()).subscribe(() => {
            this.refreshAllCounters();
        });

        this.cardViewService.assigneesStatusChangeSubject.pipe(takeUntilDestroyed()).subscribe(() => {
            this.refreshAllCounters();
        });

        // Drag and Drop support
        this.dragAndDropService.dragStarted.pipe(takeUntilDestroyed()).subscribe(() => {
            if (this.cardViewService.currentViewDisplayMode === "Kanban") {
                this.notStartedStatus.set(this.cardDragAndDropService.getFirstValidStatusOf("notStarted"));
                this.activeStatus.set(this.cardDragAndDropService.getFirstValidStatusOf("active"));
                this.completedStatus.set(this.cardDragAndDropService.getFirstValidStatusOf("completed"));
            }
        });

        this.init();
    }

    async init() {
        this.currentDashboard = await this.dashboardsService.getCurrentDashboard();
        await this.helpersService.mergeSteps();
        this.showLastYear = !isiFirmTax(this.currentDashboard.ApplicationType) || this.currentDashboard.TaxYear >= 2024;
        this.statsVisible = JSON.parse(window.localStorage.getItem("header_statsVisible")) ?? true;
        this.isAllView = this.cardViewService?.currentViewType === "all";
        this.refreshAllCounters();
    }

    private async refreshAllCounters() {
        this.showLoadingStatistics = true;
        this.refreshCounters();
        this.refreshPieChart();
        await this.refreshActiveBarChart();
        await this.refreshAlertCenter();
        this.showLoadingStatistics = false;
    }

    refreshCounters(): void {
        this.currentCounters = {
            notStarted:
                this.countersService.statisticsModel
                    ?.filter((it) => it.state === "notStarted")
                    ?.reduce((sum, it) => {
                        return sum + it.count;
                    }, 0) ?? 0,
            active:
                this.countersService.statisticsModel
                    ?.filter((it) => it.state === "active")
                    ?.reduce((sum, it) => {
                        return sum + it.count;
                    }, 0) ?? 0,
            completed:
                this.countersService.statisticsModel
                    ?.filter((it) => it.state === "completed")
                    ?.reduce((sum, it) => {
                        return sum + it.count;
                    }, 0) ?? 0,
            all:
                this.countersService.statisticsModel?.reduce((sum, it) => {
                    return sum + it.count;
                }, 0) ?? 0,
        };
        this.currentTotal = this.currentCounters.notStarted + this.currentCounters.active + this.currentCounters.completed;

        this.lastYearCountersByState = {
            notStarted:
                this.countersService.statisticsModel
                    ?.filter((it) => it.state === "notStarted")
                    ?.reduce((sum, it) => {
                        return sum + it.lastYearCount;
                    }, 0) ?? 0,
            active:
                this.countersService.statisticsModel
                    ?.filter((it) => it.state === "active")
                    ?.reduce((sum, it) => {
                        return sum + it.lastYearCount;
                    }, 0) ?? 0,
            completed:
                this.countersService.statisticsModel
                    ?.filter((it) => it.state === "completed")
                    ?.reduce((sum, it) => {
                        return sum + it.lastYearCount;
                    }, 0) ?? 0,
        };

        this.variance = {
            notStarted: this.computeVariance(this.currentCounters.notStarted, this.lastYearCountersByState.notStarted),
            active: this.computeVariance(this.currentCounters.active, this.lastYearCountersByState.active),
            completed: this.computeVariance(this.currentCounters.completed, this.lastYearCountersByState.completed),
        };

        // Calculate percentages.
        if (this.currentTotal > 0) {
            let notStartedRatio = (this.currentCounters.notStarted / this.currentTotal) * 100;
            let activeRatio = (this.currentCounters.active / this.currentTotal) * 100;
            let completedRatio = (this.currentCounters.completed / this.currentTotal) * 100;

            const totalInitialPercentage: number = notStartedRatio + activeRatio + completedRatio;
            // Calculate the adjustment needed to make the total percentage 100%
            const adjustmentFactor: number = 100 / totalInitialPercentage;
            // Adjust the percentages
            notStartedRatio *= adjustmentFactor;
            activeRatio *= adjustmentFactor;
            completedRatio *= adjustmentFactor;

            // Round percentages to one decimal place and ensure they sum to 100%
            let roundedPercentageNotStartedRatio: number = Math.round(notStartedRatio * 10) / 10;
            const roundedPercentageActiveRatio: number = Math.round(activeRatio * 10) / 10;
            const roundedPercentageCompletedRatio: number = Math.round(completedRatio * 10) / 10;

            // Adjust the percentages to ensure the total exactly 100%
            const totalRoundedPercentage: number = roundedPercentageNotStartedRatio + roundedPercentageActiveRatio + roundedPercentageCompletedRatio;
            const roundingDifference: number = 100 - totalRoundedPercentage;
            // Distribute the rounding difference to the first bucket
            roundedPercentageNotStartedRatio = roundedPercentageNotStartedRatio + roundingDifference;

            this.countersPercentages = {
                notStarted: roundedPercentageNotStartedRatio.toFixed(1),
                active: roundedPercentageActiveRatio.toFixed(1),
                completed: roundedPercentageCompletedRatio.toFixed(1),
            };
        }
    }

    resetCounters() {
        this.currentCounters = {
            notStarted: 0,
            active: 0,
            completed: 0,
            all: 0,
        };
        this.countersPercentages = {
            notStarted: "0",
            active: "0",
            completed: "0",
        };
        this.currentTotal = 0;
        this.variance = {
            notStarted: 0,
            active: 0,
            completed: 0,
        };
        this.lastYearCountersByState = {
            notStarted: 0,
            active: 0,
            completed: 0,
        };
        this.cardsWithAlert = 0;
        this.cardsWithWarning = 0;
    }

    refreshPieChart(): void {
        this.pieChartConfiguration = {
            type: "Pie",
            data: {
                series: this.currentTotal == 0 ? ["33.4", "33.3", "33.3"] : Object.values(this.countersPercentages),
            },
            options: {
                donut: true,
                donutWidth: 10,
                total: 100,
                showLabel: false,
                height: 130,
                width: 130,
            },
        };
    }

    async refreshActiveBarChart() {
        if (this.countersService.statisticsModel) {
            const barData = await this.generateChartData();

            this.activeBarConfiguration = {
                type: "Bar",
                data: barData,
                options: {
                    seriesBarDistance: 200,
                    axisX: {
                        showGrid: false,
                        showLabel: true,
                        labelOffset: {
                            x: 0,
                            y: 2,
                        },
                        stretch: false,
                        labelInterpolationFnc: (value: string) => value?.toUpperCase() ?? "", // TODO We did not find the step, but we should have it at this point.
                    },
                    axisY: {
                        showGrid: true,
                        labelInterpolationFnc: (value: number) => {
                            return value === this.barChartHigh ? value : "";
                        },
                        type: FixedScaleAxis,
                        ticks:
                            this.barChartHigh <= 4
                                ? Array.from({ length: this.barChartHigh + 1 }, (_, i) => i)
                                : [
                                      0,
                                      (1 * this.barChartHigh) / 5,
                                      (2 * this.barChartHigh) / 5,
                                      (3 * this.barChartHigh) / 5,
                                      (4 * this.barChartHigh) / 5,
                                      this.barChartHigh,
                                  ],
                        showLabel: true,
                        position: "end",
                    },
                },
            };

            this.setGraphWidth(barData.labels.length);
            this.showGraph = this.barChartHigh != 0;
        }
    }

    async refreshAlertCenter() {
        const state = this.cardViewService.currentViewType;
        if (this.cardViewService.currentViewDisplayMode === "details") {
            // TODO : we need to have a better way of fetching that value.
            const LocalStorageKey = this.authService.currentUser()?.sub + (await this.dashboardsService.getSafeCurrentDashboardId()) + state;
            const cachedStepId = localStorage.getItem(LocalStorageKey + "StepID");
            const selectedStatusId = localStorage.getItem(LocalStorageKey + "StepReturnStatus" + cachedStepId);
            this.cardsWithWarning =
                this.countersService.statisticsModel
                    ?.filter((it) => it.statusId === selectedStatusId)
                    ?.reduce((sum, it) => {
                        return sum + it.warningCount;
                    }, 0) ?? 0;
            this.cardsWithAlert =
                this.countersService.statisticsModel
                    ?.filter((it) => it.statusId === selectedStatusId)
                    ?.reduce((sum, it) => {
                        return sum + it.alertCount;
                    }, 0) ?? 0;
        } else {
            this.cardsWithWarning =
                this.countersService.statisticsModel
                    ?.filter((it) => it.state === state)
                    ?.reduce((sum, it) => {
                        return sum + it.warningCount;
                    }, 0) ?? 0;
            this.cardsWithAlert =
                this.countersService.statisticsModel
                    ?.filter((it) => it.state === state)
                    ?.reduce((sum, it) => {
                        return sum + it.alertCount;
                    }, 0) ?? 0;
        }
    }

    async generateChartData(): Promise<{
        labels: any[];
        series: any[][];
        Id: any[];
    }> {
        const state = this.cardViewService.currentViewType;
        const steps = await this.stepsService.getStepsList();
        const data = this.countersService.statisticsModel
            .filter((model) => model.state === state && model.stepId != undefined)
            .map((model) => {
                return {
                    label: steps.find((step) => step.Id === model.stepId)?.Name[this.currentLanguage][0],
                    count: model.count,
                    Id: model.stepId,
                };
            })
            .reduce(
                (acc, it) => {
                    if (acc.Id.includes(it.Id)) {
                        acc.series[0][acc.Id.indexOf(it.Id)] += it.count;
                    } else {
                        acc.Id.push(it.Id);
                        acc.labels.push(it.label);
                        acc.series[0].push(it.count);
                    }
                    return acc;
                },
                {
                    labels: [],
                    series: [[]],
                    Id: [],
                },
            );
        this.barChartHigh = data.series[0].reduce((max, num) => Math.max(max, num), 0);
        return data;
    }

    setGraphWidth(length: number): void {
        this.barChartWidth = Math.max(length * 39.5, 200);
    }

    computeVariance(a: number, b: number) {
        let variance = ((a - b) / b) * 100;
        if (Number.isNaN(variance) || variance == Number.POSITIVE_INFINITY || variance == Number.NEGATIVE_INFINITY) {
            variance = 0;
        }
        return variance;
    }

    toggleStatsVisible() {
        this.statsVisible = !this.statsVisible;
        LocalStorageService.setItem("header_statsVisible", JSON.stringify(this.statsVisible));

        this.analyticsService.track("Collapse/Expand statistics section", {
            isCollapsed: !this.statsVisible,
            viewName: this.getViewTypeDisplayName(this.cardViewService.currentViewType),
        });
    }

    private getViewTypeDisplayName(viewType: string): string {
        switch (viewType) {
            case "notStarted":
                return "Not started";
            case "active":
                return "Active";
            case "completed":
                return "Completed";
            default:
                return "All";
        }
    }

    protected onDrop($event: CdkDragDrop<unknown>, statusId: string) {
        const card: IBaseCard = $event.item.data;

        if (!$event.isPointerOverContainer) return;
        this.cardDragAndDropService.onDrop(card, statusId);
    }
}
